import React, { useMemo } from 'react';
import useElementSize from '../hooks/useElementSize.js';

const ViolinPlotViewer = React.memo(function ViolinPlotViewer({ table, categoryCol, numericCol, searchIndices, colorConfig, isLogScale }) {
  const [containerRef, { width, height }] = useElementSize({ debounceMs: 50 });
  const isDateCol = useMemo(() => /date/i.test(numericCol), [numericCol]);

  const data = useMemo(() => {
    if (!table || !categoryCol || !numericCol) return null;
    const catData = table.getChild(categoryCol);
    const numData = table.getChild(numericCol);
    if (!catData || !numData) return null;

    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);
    
    const freqs = new Map();
    const catCounts = new Map(); // Track true categorical frequency for sorting

    for (let i = 0; i < source.length; i++) {
      const cat = catData.get(source[i]);
      if (cat != null && cat !== '') {
        const catStr = String(cat);
        catCounts.set(catStr, (catCounts.get(catStr) || 0) + 1);

        const rawNum = numData.get(source[i]);
        let num;
        if (isDateCol) {
          num = new Date(rawNum).getTime();
        } else {
          num = typeof rawNum === 'number' ? rawNum : parseFloat(rawNum);
        }
        
         if (num != null && !isNaN(num) && isFinite(num)) {
          if (isLogScale && !isDateCol) {
            if (num > 0) {
              if (!freqs.has(catStr)) freqs.set(catStr, []);
              freqs.get(catStr).push(Math.log10(num));
            }
          } else {
            if (!freqs.has(catStr)) freqs.set(catStr, []);
            freqs.get(catStr).push(num);
          }
        }
      }
    }

    // Limit to 8 most popular categories
    const topCategories = Array.from(freqs.entries())
      .sort((a, b) => (catCounts.get(b[0]) || 0) - (catCounts.get(a[0]) || 0))
      .slice(0, 8);

    if (topCategories.length === 0) return null;

    let globalMin = Infinity;
    let globalMax = -Infinity;

    // First pass to find medians, quartiles, and global bounds
    const stats = topCategories.map(([cat, vals]) => {
      vals.sort((a, b) => a - b);
      const n = vals.length;
      const q1 = vals[Math.floor(n * 0.25)];
      const median = vals[Math.floor(n * 0.5)];
      const q3 = vals[Math.floor(n * 0.75)];
      
      const min = vals[0];
      const max = vals[n - 1];

      if (min < globalMin) globalMin = min;
      if (max > globalMax) globalMax = max;

      return { cat, vals, q1, median, q3, min, max, n };
    });

    const padding = (globalMax - globalMin) * 0.05 || 1;
    globalMin -= padding;
    globalMax += padding;
    const globalRange = globalMax - globalMin;

    // Second pass to compute KDE for the violin shapes
    const violins = stats.map(stat => {
      const { cat, vals, q1, median, q3, min, max, n } = stat;

      // Calculate standard deviation
      const mean = vals.reduce((sum, v) => sum + v, 0) / n;
      const variance = vals.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
      const sd = Math.sqrt(variance);
      const iqr = q3 - q1;

      // Silverman's Rule of Thumb for bandwidth selection
      let bandwidth = 0.9 * Math.min(sd, iqr > 0 ? iqr / 1.34 : sd) * Math.pow(n, -0.2);
      if (bandwidth === 0 || isNaN(bandwidth)) {
        bandwidth = globalRange / 20; // Fallback for highly uniform data
      }
      // Enforce a minimum bandwidth so KDE doesn't vanish into sharp un-renderable spikes
      bandwidth = Math.max(bandwidth, globalRange / 100);

      const steps = 400; // Resolution of the violin curves
      const pad = bandwidth * 3;
      const rangeMin = Math.max(globalMin, min - pad);
      const rangeMax = Math.min(globalMax, max + pad);

      const thresholds = Array.from({ length: steps }, (_, i) => rangeMin + i * (rangeMax - rangeMin) / (steps - 1));

      const density = thresholds.map(t => {
        let sum = 0;
        for (let i = 0; i < n; i++) {
          const u = (t - vals[i]) / bandwidth;
          if (Math.abs(u) <= 1) { // Epanechnikov kernel
            sum += 0.75 * (1 - u * u);
          }
        }
        return { val: t, density: sum / (n * bandwidth) };
      });

      // Clamp ends to 0 so the SVG path closes smoothly
      density.unshift({ val: rangeMin - 1e-9, density: 0 });
      density.push({ val: rangeMax + 1e-9, density: 0 });

      const maxDensity = Math.max(...density.map(d => d.density));

      return { cat, q1, median, q3, min, max, density, maxDensity };
    });

    return { violins, globalMin, globalMax };
  }, [table, categoryCol, numericCol, searchIndices, isDateCol, isLogScale]);

  if (!data || data.violins.length === 0) return <div className="flex items-center justify-center h-full text-gray-500 text-xs">No numeric data available</div>;

  const padding = { top: 10, right: 10, bottom: 30, left: isDateCol ? 65 : 45 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  
  if (innerWidth <= 0 || innerHeight <= 0) return <div ref={containerRef} className="w-full h-full" />;

  const scaleY = (val) => innerHeight - ((val - data.globalMin) / (data.globalMax - data.globalMin)) * innerHeight;
  const bandWidth = innerWidth / data.violins.length;
  // Make the violin slightly wider than the original box plots to look better visually
  const violinWidth = Math.min(bandWidth * 0.8, 80);

  // Calculate dynamic label size and truncation based on available space per category
  const labelFontSize = bandWidth < 40 ? 8 : (bandWidth < 60 ? 9 : 10);
  const charWidth = labelFontSize * 0.65; // Approx pixel width of a bold character
  const maxChars = Math.max(3, Math.floor((bandWidth - 4) / charWidth));

  // Calculate ticks
  const ticks = [];
  for (let i = 0; i <= 5; i++) {
    ticks.push(data.globalMin + (data.globalMax - data.globalMin) * (i / 5));
  }

  // Generate symmetrical SVG path for the violin curve
  const getViolinPath = (violin, cx) => {
    const scaleX = (violinWidth / 2) / (violin.maxDensity || 1);
    const rightSide = violin.density.map(d => `${cx + d.density * scaleX},${scaleY(d.val)}`);
    const leftSide = violin.density.map(d => `${cx - d.density * scaleX},${scaleY(d.val)}`).reverse();
    return `M ${rightSide.join(' L ')} L ${leftSide.join(' L ')} Z`;
  };

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <svg width={width} height={height} className="absolute top-0 left-0">
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {ticks.map((tick, i) => {
            let tickLabel;
            if (isDateCol) {
              const d = new Date(tick);
              tickLabel = !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
            } else if (isLogScale) {
              const raw = Math.pow(10, tick);
              tickLabel = raw < 0.01 || raw >= 10000 ? raw.toExponential(1) : parseFloat(raw.toPrecision(3)).toString();
            } else {
              tickLabel = tick.toLocaleString(undefined, {maximumFractionDigits: 1});
            }
            return (
              <g key={i} transform={`translate(0, ${scaleY(tick)})`}>
                <line x1={0} y1={0} x2={innerWidth} y2={0} stroke="#e5e7eb" strokeDasharray="4,4" />
                <text x={-5} y={3} textAnchor="end" fontSize={9} fill="#9ca3af">{tickLabel}</text>
              </g>
            );
          })}
          <line x1={0} y1={0} x2={0} y2={innerHeight} stroke="#d1d5db" />
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
          
          {data.violins.map((violin, i) => {
            const cx = i * bandWidth + bandWidth / 2;
            const color = colorConfig?.colorMap?.get(violin.cat) || [59, 130, 246, 200];
            const fill = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.4)`;
            const stroke = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

            return (
              <g key={violin.cat}>
                {/* Violin KDE Shape */}
                <path d={getViolinPath(violin, cx)} fill={fill} stroke={stroke} strokeWidth={1.5} />
                
                {/* Inner min/max whisker line */}
                <line x1={cx} y1={scaleY(violin.max)} x2={cx} y2={scaleY(violin.min)} stroke="#4b5563" strokeWidth={1} />
                
                {/* Inner IQR heavy rect line */}
                <line x1={cx} y1={scaleY(violin.q3)} x2={cx} y2={scaleY(violin.q1)} stroke="#4b5563" strokeWidth={4} strokeLinecap="round" />
                
                {/* Inner Median Dot */}
                <circle cx={cx} cy={scaleY(violin.median)} r={2.5} fill="white" stroke="#4b5563" strokeWidth={1.5} />
                
                <text x={cx} y={innerHeight + 15} textAnchor="middle" fontSize={labelFontSize} fill="#4b5563" fontWeight="bold">
                   <title>{violin.cat}</title>                  
                  {violin.cat.length > maxChars ? violin.cat.substring(0, Math.max(1, maxChars - 2)) + '..' : violin.cat}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
});

export default ViolinPlotViewer;